import React, { useState } from 'react';
import { useCourseCreation } from '../../../contexts/CourseCreationContext';
import {
  Code,
  Plus,
  Trash2,
  Save,
  Eye,
  AlertCircle,
  TestTube
} from 'lucide-react';

const ExerciseEditor = ({ content, onUpdate, onSave, onPreview }) => {
  const { validateContent } = useCourseCreation();
  const [errors, setErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);

  const handleFieldChange = (field, value) => {
    const updatedContent = { ...content, [field]: value };
    onUpdate(updatedContent);
    
    const validation = validateContent(updatedContent, 'exercise');
    setErrors(validation.errors);
  };

  const handleSave = async () => {
    setIsValidating(true);
    const validation = validateContent(content, 'exercise');
    
    if (validation.valid) {
      await onSave(content);
      setErrors([]);
    } else {
      setErrors(validation.errors);
    }
    setIsValidating(false);
  };

  const addTestCase = () => {
    const newTestCase = {
      input: '',
      expected: '',
      description: ''
    };
    
    const updatedTestCases = [...(content.testCases || []), newTestCase];
    handleFieldChange('testCases', updatedTestCases);
  };

  const updateTestCase = (index, updates) => {
    const updatedTestCases = (content.testCases || []).map((testCase, i) =>
      i === index ? { ...testCase, ...updates } : testCase
    );
    handleFieldChange('testCases', updatedTestCases);
  };

  const removeTestCase = (index) => {
    const updatedTestCases = (content.testCases || []).filter((_, i) => i !== index);
    handleFieldChange('testCases', updatedTestCases);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Code className="w-6 h-6 text-green-500" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Éditeur d'Exercice Pratique
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPreview(content)}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <Eye className="w-4 h-4" />
            Aperçu
          </button>
          <button
            onClick={handleSave}
            disabled={isValidating || errors.length > 0}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4" />
            {isValidating ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* Erreurs */}
      {errors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
              Erreurs de validation
            </h3>
          </div>
          <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
            {errors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulaire */}
        <div className="space-y-6">
          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Titre de l'exercice *
            </label>
            <input
              type="text"
              value={content.title || ''}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              placeholder="Ex: Calculer la somme de deux nombres"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description *
            </label>
            <textarea
              value={content.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Décrivez l'objectif de cet exercice..."
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
            />
          </div>

          {/* Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Instructions détaillées *
            </label>
            <textarea
              value={content.instructions || ''}
              onChange={(e) => handleFieldChange('instructions', e.target.value)}
              placeholder="Donnez des instructions claires pour résoudre l'exercice..."
              rows="4"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
            />
          </div>

          {/* Code de départ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Code de départ
            </label>
            <textarea
              value={content.starterCode || ''}
              onChange={(e) => handleFieldChange('starterCode', e.target.value)}
              rows="8"
              placeholder="// Code de départ pour les étudiants..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm resize-none"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Code de base fourni aux étudiants pour commencer l'exercice
            </p>
          </div>
        </div>

        {/* Tests et aperçu */}
        <div className="space-y-6">
          {/* Tests de validation */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Tests de validation
              </h3>
              <button
                onClick={addTestCase}
                className="flex items-center gap-2 px-3 py-1 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                Ajouter un test
              </button>
            </div>

            <div className="space-y-3">
              {(content.testCases || []).map((testCase, index) => (
                <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      Test {index + 1}
                    </h4>
                    <button
                      onClick={() => removeTestCase(index)}
                      className="p-1 text-red-500 hover:text-red-700 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={testCase.description}
                        onChange={(e) => updateTestCase(index, { description: e.target.value })}
                        placeholder="Description du test"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Entrée
                      </label>
                      <input
                        type="text"
                        value={testCase.input}
                        onChange={(e) => updateTestCase(index, { input: e.target.value })}
                        placeholder="Valeur d'entrée"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Sortie attendue
                      </label>
                      <input
                        type="text"
                        value={testCase.expected}
                        onChange={(e) => updateTestCase(index, { expected: e.target.value })}
                        placeholder="Résultat attendu"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Aperçu */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Aperçu de l'exercice
            </h3>
            
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                {content.title || 'Titre de l\'exercice'}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {content.description || 'Description de l\'exercice'}
              </p>
              
              {content.starterCode && (
                <div className="mb-3">
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Code de départ :
                  </h5>
                  <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded border overflow-x-auto">
                    <code>{content.starterCode}</code>
                  </pre>
                </div>
              )}
              
              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center gap-1">
                  <TestTube className="w-3 h-3" />
                  <span>{(content.testCases || []).length} tests</span>
                </div>
                <div className="flex items-center gap-1">
                  <Code className="w-3 h-3" />
                  <span>Exercice de code</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseEditor;
